import { useEffect, useRef, useState } from 'react'

import { Button } from '../components/Button.js'

/**
 * "That's 10 games played."
 *
 * Shown at the end of the round that crossed it, on the screen she just
 * finished on, because the moment is the point. Once, warmly, and then out of
 * the way — no badge shelf, no trophy case, nothing that quietly becomes a
 * collection to complete.
 *
 * There is no rank and nothing to compare, which is what makes this one safe
 * to celebrate at all: to have played more games you have to have played more
 * games. The cheat and the goal are the same thing.
 *
 * **Over the round rather than under it.** It used to sit inline beneath the
 * result, competing with the score, the race and the way to start another at
 * the noisiest moment of the round — so the one thing worth stopping for was
 * the easiest thing to miss. It is a panel over the playing area now, and she
 * puts it away herself.
 *
 * Not a live region of its own, deliberately. Each game screen has exactly one
 * (see Announce), and a second one appearing at the end of a round is two
 * announcements racing in the same tick — the milestone could be read out
 * before the result of the round it came from. The game's own region says this
 * instead, in its place in the order.
 */
export function Milestone({ games }: { games: number }) {
  const acknowledge = useRef<HTMLButtonElement>(null)
  /*
   * Put away here rather than by the game underneath.
   *
   * Seven call sites already render this the same way, and handing each of
   * them a piece of state to keep would be seven chances to keep it wrongly.
   * It resets by itself: a new round clears the award, this unmounts, and the
   * next milestone arrives on a fresh one.
   */
  const [seen, setSeen] = useState(false)

  /*
   * Focus goes to the one thing there is to do.
   *
   * This is what makes the whole panel work with no key handling of its own.
   * `useGameKeys` already steps aside for Enter and Space when a button has
   * focus — that is how a game avoids swallowing the key from somebody who has
   * tabbed to the way home — so with focus here the browser presses this
   * button and the game underneath never sees the key.
   *
   * Which closes the obvious trap: one Enter dismissing the milestone *and*
   * starting the next round, so she never reads it. There is a test for
   * exactly that, because it is the kind of thing that works by accident until
   * somebody adds a keydown listener.
   */
  useEffect(() => {
    acknowledge.current?.focus()
  }, [])

  if (seen) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="milestone-heading"
      /*
       * Over the whole viewport rather than inside the round's card.
       *
       * Anchoring it to the card would need a positioning context in all seven
       * games — seven edits to put one panel on the screen, and a silent
       * misplacement in whichever one got missed. Covering the page needs
       * nothing from them at all, and a panel that covers the page is what a
       * thing you have to acknowledge should do.
       */
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 p-4"
    >
      <div className="border-amber bg-amber-soft text-ink flex max-w-lg flex-col items-center gap-2 rounded-(--radius-card) border-2 px-5 py-4 text-center">
        {/*
          A drawn star rather than an emoji.

          The one bit of movement in the app, and only for somebody who has not
          asked for less — the theme kills it under prefers-reduced-motion too,
          and this says so at the point of use as well.

          Drawn because a Raspberry Pi has no emoji font, and a missing glyph is
          a rectangle. Anything that has to appear on the television is drawn or
          it is words.
        */}
        <svg
          viewBox="0 0 24 24"
          className="motion-safe:animate-bounce text-amber size-(--size-tap)"
          aria-hidden="true"
          fill="currentColor"
        >
          <path d="M12 2l2.6 6.3 6.8.5-5.2 4.4 1.6 6.6L12 16.3 6.2 19.8l1.6-6.6L2.6 8.8l6.8-.5z" />
        </svg>

        <p id="milestone-heading" className="font-display text-(length:--text-title)">
          {`That’s ${games} games played!`}
        </p>

        {/* What is being marked, said plainly, so it cannot be mistaken for a
            score she has to beat. */}
        <p className="text-ink-muted text-(length:--text-body)">
          Won or lost, every one of them counts. Thanks for keeping at it.
        </p>

        {/*
          A real button, so Enter and Space both work without this component
          listening for either — and so a finger has something to press.
        */}
        <Button ref={acknowledge} onClick={() => setSeen(true)}>
          Thanks!
        </Button>
      </div>
    </div>
  )
}
